// Prompt 0454 — Tab Ficha con editor Tiptap por seccion (M4).
// Prompt 0456 — Migrado a ficha-shared/ (extensions + toolbar + helpers + tipos).
// Cada seccion instancia su propio useEditor via useFichaEditor. La toolbar es compartida.

import type React from 'react';
import { useState, useEffect } from 'react';
import { EditorContent } from '@tiptap/react';
import {
  fetchFichaContenido,
  actualizarFichaContenido,
  crearFichaPuesto,
  type FichaSeccionesInput,
} from '../../../../../../shared/services/api';
import { useI18n } from '../../../../../../../i18n';
import { LoadingSpinner } from '../../../../../../shared/components/LoadingSpinner';
import { FichaToolbar } from '../../ficha-shared/FichaToolbar';
import { useFichaEditor } from '../../ficha-shared/useFichaEditor';
import { extractSeccionesNodos } from '../../ficha-shared/ficha-nodos';
import type { HeadingToSeccionMap, ProseMirrorNode, SeccionId } from '../../ficha-shared/types';

const SECCIONES: Array<{ id: SeccionId; labelKey: string; fallbackLabel: string }> = [
  { id: 'mision', labelKey: 'fichaMision', fallbackLabel: 'Mision del puesto' },
  { id: 'funciones', labelKey: 'fichaFunciones', fallbackLabel: 'Funciones y responsabilidades' },
  {
    id: 'competenciasTecnicas',
    labelKey: 'fichaCompetenciasTecnicas',
    fallbackLabel: 'Competencias tecnicas',
  },
  {
    id: 'competenciasTransversales',
    labelKey: 'fichaCompetenciasTransversales',
    fallbackLabel: 'Competencias transversales',
  },
  { id: 'formacion', labelKey: 'fichaFormacion', fallbackLabel: 'Formacion requerida' },
  { id: 'experiencia', labelKey: 'fichaExperiencia', fallbackLabel: 'Experiencia requerida' },
  {
    id: 'certificaciones',
    labelKey: 'fichaCertificaciones',
    fallbackLabel: 'Certificaciones obligatorias',
  },
  { id: 'condiciones', labelKey: 'fichaCondiciones', fallbackLabel: 'Condiciones del puesto' },
];

interface FichaTabProps {
  puestoId: string;
  hasFicha: boolean;
  isDirty: boolean;
  onMarkDirty: () => void;
  onMarkClean: () => void;
  onCreated: () => Promise<void> | void;
}

// Mapea heading → seccionId para la plantilla de FP (backend fichas-puesto.service.ts).
// Parametro de extractSeccionesNodos (shared entre puesto y rol, cada uno con su map).
const HEADING_TO_SECCION: HeadingToSeccionMap = {
  'Mision del Puesto': 'mision',
  'Funciones y Responsabilidades': 'funciones',
  'Competencias tecnicas': 'competenciasTecnicas',
  'Competencias transversales': 'competenciasTransversales',
  'Formacion Academica Requerida': 'formacion',
  'Experiencia Profesional Requerida': 'experiencia',
  'Certificaciones Obligatorias': 'certificaciones',
  'Condiciones del Puesto': 'condiciones',
};

export function FichaTab({
  puestoId,
  hasFicha,
  isDirty,
  onMarkDirty,
  onMarkClean,
  onCreated,
}: FichaTabProps) {
  const { t } = useI18n();
  const adm = (t.structure as Record<string, unknown>)?.admin as
    | Record<string, unknown>
    | undefined;
  const lbl = (adm ?? {}) as Record<string, string>;
  const panel = (lbl.puestoPanel ?? {}) as unknown as Record<string, string>;

  const [secciones, setSecciones] = useState<Record<SeccionId, ProseMirrorNode[]>>(
    {} as Record<SeccionId, ProseMirrorNode[]>,
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');
  const [fichaExists, setFichaExists] = useState(hasFicha);
  const [focusedSeccion, setFocusedSeccion] = useState<SeccionId | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  useEffect(() => {
    setFichaExists(hasFicha);
    if (!hasFicha) {
      setSecciones({} as Record<SeccionId, ProseMirrorNode[]>);
      return;
    }
    setLoading(true);
    fetchFichaContenido(puestoId)
      .then((res) => {
        setSecciones(extractSeccionesNodos(res.data, HEADING_TO_SECCION));
      })
      .catch(() => setError(panel.errorCargarFicha ?? 'Error al cargar la ficha'))
      .finally(() => setLoading(false));
  }, [puestoId, hasFicha, panel.errorCargarFicha]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      await crearFichaPuesto(puestoId);
      const res = await fetchFichaContenido(puestoId);
      setSecciones(extractSeccionesNodos(res.data, HEADING_TO_SECCION));
      setFichaExists(true);
      await onCreated();
      showToast(panel.fichaCreada ?? 'Ficha creada');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setCreating(false);
    }
  };

  const handleGuardar = async () => {
    setSaving(true);
    try {
      // FichaSeccionesInput acepta `string | unknown[]` por seccion; aqui enviamos arrays de nodos.
      await actualizarFichaContenido(puestoId, secciones as FichaSeccionesInput);
      showToast(panel.fichaGuardada ?? 'Ficha guardada');
      onMarkClean();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelar = async () => {
    setError('');
    if (!fichaExists) {
      onMarkClean();
      return;
    }
    setLoading(true);
    try {
      const res = await fetchFichaContenido(puestoId);
      setSecciones(extractSeccionesNodos(res.data, HEADING_TO_SECCION));
      onMarkClean();
    } catch {
      // silenciar, se reintenta al reabrir
    } finally {
      setLoading(false);
    }
  };

  if (!fichaExists) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <p style={{ color: '#64748B', fontSize: 13, marginBottom: 16 }}>
          {panel.fichaNoExiste ?? 'Este puesto no tiene ficha de puesto.'}
        </p>
        <button
          onClick={handleCreate}
          disabled={creating}
          style={{
            padding: '10px 24px',
            background: creating ? '#94A3B8' : '#1F3864',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            cursor: creating ? 'wait' : 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {creating ? (panel.creandoFicha ?? 'Creando...') : (panel.crearFicha ?? 'Crear ficha')}
        </button>
      </div>
    );
  }

  return (
    <div>
      {toast && (
        <div
          style={{
            marginBottom: 12,
            padding: '8px 14px',
            background: '#F0FDF4',
            border: '1px solid #BBF7D0',
            borderRadius: 6,
            fontSize: 12,
            color: '#16A34A',
          }}
        >
          {toast}
        </div>
      )}
      {error && (
        <div
          style={{
            marginBottom: 12,
            padding: '8px 14px',
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: 6,
            fontSize: 12,
            color: '#DC2626',
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <LoadingSpinner size="sm" muted />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {SECCIONES.map((s) => (
            <SeccionFicha
              key={s.id}
              seccionId={s.id}
              titulo={(panel[s.labelKey] as string | undefined) ?? s.fallbackLabel}
              nodosIniciales={secciones[s.id] ?? []}
              focused={focusedSeccion === s.id}
              onFocus={() => setFocusedSeccion(s.id)}
              onUpdate={(nodos) => {
                setSecciones((prev) => ({ ...prev, [s.id]: nodos }));
                onMarkDirty();
              }}
            />
          ))}
        </div>
      )}

      <div style={footerSticky}>
        <button onClick={handleCancelar} disabled={!isDirty} style={btnSecondary(!isDirty)}>
          {panel.cancelarFicha ?? 'Cancelar'}
        </button>
        <button
          onClick={handleGuardar}
          disabled={!isDirty || saving}
          style={btnPrimary(!isDirty || saving)}
        >
          {saving
            ? (panel.saving ?? 'Guardando...')
            : (panel.guardarFichaTab ?? panel.guardarFicha ?? 'Guardar ficha')}
        </button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Seccion individual con Tiptap
// ----------------------------------------------------------------------------

interface SeccionFichaProps {
  seccionId: SeccionId;
  titulo: string;
  nodosIniciales: ProseMirrorNode[];
  focused: boolean;
  onFocus: () => void;
  onUpdate: (nodos: ProseMirrorNode[]) => void;
}

function SeccionFicha({ titulo, nodosIniciales, focused, onFocus, onUpdate }: SeccionFichaProps) {
  // 0456: useFichaEditor encapsula useEditor + extensions Tiptap consolidadas.
  const editor = useFichaEditor({ nodosIniciales, onUpdate, onFocus });

  return (
    <section>
      <h3
        style={{
          margin: '0 0 6px',
          fontSize: 12,
          fontWeight: 700,
          color: '#1F3864',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {titulo}
      </h3>
      {focused && editor && <FichaToolbar editor={editor} />}
      <div
        style={{
          border: '1px solid #E5E7EB',
          borderTopLeftRadius: focused ? 0 : 6,
          borderTopRightRadius: focused ? 0 : 6,
          borderBottomLeftRadius: 6,
          borderBottomRightRadius: 6,
          padding: 12,
          minHeight: 90,
          background: '#fff',
          fontSize: 13,
          lineHeight: 1.5,
        }}
      >
        <EditorContent editor={editor} />
      </div>
    </section>
  );
}

const footerSticky: React.CSSProperties = {
  position: 'sticky',
  bottom: 0,
  marginTop: 20,
  padding: '12px 0',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 10,
  background: '#fff',
  borderTop: '1px solid #E5E7EB',
};

const btnSecondary = (disabled: boolean): React.CSSProperties => ({
  padding: '8px 20px',
  background: 'transparent',
  border: '1px solid #D1D5DB',
  borderRadius: 6,
  fontSize: 13,
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontFamily: 'inherit',
  color: disabled ? '#9CA3AF' : '#374151',
  opacity: disabled ? 0.6 : 1,
});

const btnPrimary = (disabled: boolean): React.CSSProperties => ({
  padding: '8px 24px',
  background: disabled ? '#94A3B8' : '#16A34A',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 700,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.7 : 1,
  fontFamily: 'inherit',
});
